/**
 * PostHog Metrics Middleware
 *
 * RTK Listener Middleware for capturing Redux action events and sending to PostHog.
 * Integrates with analyticsSlice view actions to track user interactions.
 *
 * Usage:
 * 1. Call initPosthogMiddleware(gateway) before store creation
 * 2. Add posthogMetricsMiddleware.middleware to store middleware
 * 3. Dispatch analyticsSlice view actions from components
 *
 * Story 4.1: PostHog Frontend Analytics - Phase 4: Core Event Listeners
 */

import {
  createListenerMiddleware,
  TypedStartListening,
} from "@reduxjs/toolkit";
import type { IAnalyticsGateway } from "@/domain/ports/IAnalyticsGateway";
import { captureWithDeduplication, initDeduplication } from "./deduplication.utils";
import { createBasePayload } from "./payload.utils";
import { PostHogEvents } from "./events.enum";

// ============================================
// Slice Action Imports
// ============================================

// Authentication actions
import {
  loginWithEmail,
  handleOAuthCallback,
  logoutUser,
} from "@/store/slices/authSlice";

// Signup actions
import { confirmSignUpAsync } from "@/store/slices/signupSlice";

// Analytics view actions
import {
  viewSettings,
  viewLogin,
  viewProfile,
  viewTrophyRoom,
} from "@/store/slices/analyticsSlice";

// Pairing actions
import { pairImmediatelyAsync, getScanContextAsync } from "@/store/slices/pairingSlice";

// Trophy Room actions
import {
  getUserItemsAsync,
  updateItemVisibilityAsync,
  unpairItemAsync,
} from "@/store/slices/trophyRoomSlice";

// Profile actions
import {
  addWalletAsync,
  addSocialLinkAsync,
} from "@/store/slices/profileSlice";

// Public scan action
import { fetchPublicScanContext } from "@/store/slices/publicScanSlice";

// ============================================
// Type Definitions
// ============================================

/**
 * Minimal RootState shape required by middleware
 * Full type is imported dynamically to avoid circular dependencies
 */
interface MinimalRootState {
  auth?: {
    user?: {
      customUserId?: string;
      email?: string;
    } | null;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AppDispatch = any;

// ============================================
// Listener Middleware
// ============================================

/**
 * Create the RTK Listener Middleware for PostHog metrics
 */
export const posthogMetricsMiddleware = createListenerMiddleware();

/**
 * Typed start listening for type safety
 */
export type AppStartListening = TypedStartListening<MinimalRootState, AppDispatch>;
export const startAppListening = posthogMetricsMiddleware.startListening as AppStartListening;

// ============================================
// Module State
// ============================================

/**
 * Reference to analytics gateway (injected at store creation)
 */
let analyticsGatewayRef: IAnalyticsGateway | null = null;

// ============================================
// Initialization
// ============================================

/**
 * Initialize the PostHog middleware with dependencies
 *
 * Must be called before middleware is used.
 * Typically called during store creation.
 *
 * @param gateway - Analytics gateway instance (PostHog or Fake for testing)
 */
export function initPosthogMiddleware(gateway: IAnalyticsGateway): void {
  analyticsGatewayRef = gateway;
  initDeduplication(gateway);
}

// ============================================
// User Context Helpers
// ============================================

/**
 * Get user context from Redux state for event payloads
 *
 * Extracts user ID, email, and language from the current state.
 * Returns undefined values if user is not authenticated.
 */
function getUserContext(state: MinimalRootState): {
  userId?: string;
  userEmail?: string;
  language?: string;
} {
  const auth = state.auth;

  return {
    userId: auth?.user?.customUserId,
    userEmail: auth?.user?.email,
    language: "en", // Default language - settings slice can override
  };
}

/**
 * Helper to create event payload with user context
 *
 * Combines base payload (timestamp, user context) with additional properties.
 * Used by middleware listeners to build complete event payloads.
 *
 * @param state - Current Redux state
 * @param additionalProps - Additional properties to include in payload
 * @returns Complete event payload with user context
 */
export function createEventPayload(
  state: MinimalRootState,
  additionalProps?: Record<string, unknown>
): Record<string, unknown> {
  const userContext = getUserContext(state);
  const basePayload = createBasePayload(userContext);

  return {
    ...basePayload,
    ...additionalProps,
  };
}

// ============================================
// Event Capture
// ============================================

/**
 * Capture an analytics event with deduplication
 *
 * Sends event to PostHog via the analytics gateway.
 * Returns false if gateway not initialized or event was deduplicated.
 *
 * @param eventName - Event name (from PostHogEvents enum)
 * @param payload - Event payload
 * @returns true if event was captured, false otherwise
 */
export function captureAnalyticsEvent(
  eventName: string,
  payload: Record<string, unknown>
): boolean {
  if (!analyticsGatewayRef) {
    if (import.meta.env.DEV) {
      console.warn("PostHog middleware: Analytics gateway not initialized");
    }
    return false;
  }

  return captureWithDeduplication(eventName, payload);
}

// ============================================
// Testing Utilities
// ============================================

/**
 * Get the current analytics gateway reference
 *
 * Exposed for testing purposes only.
 * @internal
 */
export function getAnalyticsGateway(): IAnalyticsGateway | null {
  return analyticsGatewayRef;
}

/**
 * Reset middleware state
 *
 * Clears the analytics gateway reference.
 * Use in test teardown to ensure clean state between tests.
 * @internal
 */
export function resetMiddleware(): void {
  analyticsGatewayRef = null;
}

// ============================================
// Event Listeners
// ============================================

/**
 * Register all analytics event listeners
 *
 * Call this function after middleware is created to set up all event listeners.
 * Called automatically during module initialization.
 */
function registerEventListeners(): void {
  // ============================================
  // Authentication Events
  // ============================================

  /**
   * user_logged_in - Email login success
   */
  startAppListening({
    actionCreator: loginWithEmail.fulfilled,
    effect: (_action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        authProvider: "email",
      });
      captureAnalyticsEvent(PostHogEvents.USER_LOGGED_IN, payload);
    },
  });

  /**
   * user_logged_in - OAuth callback success (Google/Apple)
   */
  startAppListening({
    actionCreator: handleOAuthCallback.fulfilled,
    effect: (_action, { getState }) => {
      const state = getState();
      // OAuth provider is not directly available in the action payload,
      // so we mark it as "oauth" (could be google or apple)
      const payload = createEventPayload(state, {
        authProvider: "oauth",
      });
      captureAnalyticsEvent(PostHogEvents.USER_LOGGED_IN, payload);
    },
  });

  /**
   * user_logged_out - Logout success
   */
  startAppListening({
    actionCreator: logoutUser.fulfilled,
    effect: (_action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state);
      captureAnalyticsEvent(PostHogEvents.USER_LOGGED_OUT, payload);
    },
  });

  /**
   * user_signed_up - Signup confirmed (verification complete)
   */
  startAppListening({
    actionCreator: confirmSignUpAsync.fulfilled,
    effect: (_action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        authProvider: "email",
      });
      captureAnalyticsEvent(PostHogEvents.USER_SIGNED_UP, payload);
    },
  });

  // ============================================
  // View Events (from analyticsSlice)
  // ============================================

  /**
   * settings_viewed - User views settings page
   */
  startAppListening({
    actionCreator: viewSettings,
    effect: (_action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state);
      captureAnalyticsEvent(PostHogEvents.SETTINGS_VIEWED, payload);
    },
  });

  /**
   * page_viewed - User views login page
   */
  startAppListening({
    actionCreator: viewLogin,
    effect: (_action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        path: "/auth/login",
      });
      captureAnalyticsEvent(PostHogEvents.PAGE_VIEWED, payload);
    },
  });

  /**
   * profile_viewed - User views profile page
   */
  startAppListening({
    actionCreator: viewProfile,
    effect: (action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        profileId: action.payload.profileId,
        isOwner: action.payload.isOwner ?? true,
      });
      captureAnalyticsEvent(PostHogEvents.PROFILE_VIEWED, payload);
    },
  });

  /**
   * trophy_room_viewed - User views Trophy Room (via analyticsSlice action)
   */
  startAppListening({
    actionCreator: viewTrophyRoom,
    effect: (action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        ownerId: action.payload.ownerId,
        isOwner: action.payload.isOwner,
        itemCount: action.payload.itemCount,
      });
      captureAnalyticsEvent(PostHogEvents.TROPHY_ROOM_VIEWED, payload);
    },
  });

  // ============================================
  // NFC Scan Events (CRITICAL for backend partner stats)
  // ============================================

  /**
   * nfc_scan - Viewer scans NFC chip (public scan)
   *
   * CRITICAL: Event schema must match docs/posthog-partner-stats-schema.md
   * - partnerId is REQUIRED
   * - eventId is OPTIONAL (only present during temporary events)
   * - productId is OPTIONAL
   * - userId is OPTIONAL (added automatically via createEventPayload)
   */
  startAppListening({
    actionCreator: fetchPublicScanContext.fulfilled,
    effect: (action, { getState }) => {
      const state = getState();
      const scanContext = action.payload;

      // Skip if scan context doesn't have partnerId (invalid signature, etc.)
      if (!scanContext.isAuthentic) {
        if (import.meta.env.DEV) {
          console.warn("PostHog: nfc_scan skipped - invalid signature response");
        }
        return;
      }

      // Extract partnerId from productInfo (required for backend stats)
      const partnerId = scanContext.productInfo?.partnerId;

      if (!partnerId) {
        if (import.meta.env.DEV) {
          console.warn(
            "PostHog: nfc_scan event missing required partnerId - scan will not be tracked for partner stats"
          );
        }
        return;
      }

      // Build payload matching backend schema
      const payload = createEventPayload(state, {
        partnerId,
        productId: scanContext.productInfo?.productId,
        // eventId is only present for ClaimedItemContextViewer during temp events
        ...(scanContext.type === "claimed" && "eventUrl" in scanContext && scanContext.eventUrl
          ? { eventId: extractEventIdFromUrl(scanContext.eventUrl) }
          : {}),
      });

      captureAnalyticsEvent(PostHogEvents.NFC_SCAN, payload);
    },
  });

  // ============================================
  // Pairing Lifecycle Events
  // ============================================

  /**
   * pairing_started - Owner initiates pairing
   *
   * Note: We track when getScanContext succeeds as "pairing started"
   * since that's when the user begins the pairing flow with product info.
   * Only tracks for unclaimed items (items that can be paired).
   */
  startAppListening({
    actionCreator: getScanContextAsync.fulfilled,
    effect: (action, { getState }) => {
      const state = getState();
      const scanContext = action.payload;

      // Only track if this is a pairable context (unclaimed item)
      if (scanContext.type !== "unclaimed") {
        return;
      }

      const payload = createEventPayload(state, {
        productId: scanContext.productInfo?.productId,
        method: "immediate", // ANYW3AR uses immediate pairing
      });
      captureAnalyticsEvent(PostHogEvents.PAIRING_STARTED, payload);
    },
  });

  /**
   * pairing_completed - Pairing succeeded
   */
  startAppListening({
    actionCreator: pairImmediatelyAsync.fulfilled,
    effect: (action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        productId: action.payload.productInfo?.productId,
        success: true,
      });
      captureAnalyticsEvent(PostHogEvents.PAIRING_COMPLETED, payload);
    },
  });

  /**
   * pairing_failed - Pairing failed
   */
  startAppListening({
    actionCreator: pairImmediatelyAsync.rejected,
    effect: (action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        productId: action.meta.arg?.nfcUrl,
        errorType: action.payload?.code || "UnknownError",
        success: false,
      });
      captureAnalyticsEvent(PostHogEvents.PAIRING_FAILED, payload);
    },
  });

  // ============================================
  // Trophy Room Events
  // ============================================

  /**
   * trophy_room_viewed - Trophy Room data loaded successfully
   *
   * Alternative trigger for trophy_room_viewed based on data fetch
   */
  startAppListening({
    actionCreator: getUserItemsAsync.fulfilled,
    effect: (action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        isOwner: true, // getUserItemsAsync is for authenticated owner
        itemCount: action.payload.totalCount,
      });
      captureAnalyticsEvent(PostHogEvents.TROPHY_ROOM_VIEWED, payload);
    },
  });

  /**
   * trophy_item_visibility_changed - Owner toggles item visibility
   */
  startAppListening({
    actionCreator: updateItemVisibilityAsync.fulfilled,
    effect: (action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        productId: action.payload.productId,
        visible: action.payload.visibility === "public",
      });
      captureAnalyticsEvent(PostHogEvents.TROPHY_ITEM_VISIBILITY_CHANGED, payload);
    },
  });

  /**
   * trophy_item_unpaired - Owner unpairs item from Trophy Room
   */
  startAppListening({
    actionCreator: unpairItemAsync.fulfilled,
    effect: (action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        productId: action.payload, // unpairItemAsync returns itemId
        reason: action.meta.arg?.reportType || "unspecified",
      });
      captureAnalyticsEvent(PostHogEvents.TROPHY_ITEM_UNPAIRED, payload);
    },
  });

  // ============================================
  // Profile Events
  // ============================================

  /**
   * wallet_added - User adds wallet to profile
   */
  startAppListening({
    actionCreator: addWalletAsync.fulfilled,
    effect: (action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        walletType: action.payload.type,
        // Note: chainId could be derived from wallet type if needed
      });
      captureAnalyticsEvent(PostHogEvents.WALLET_ADDED, payload);
    },
  });

  /**
   * social_link_added - User adds social link to profile
   */
  startAppListening({
    actionCreator: addSocialLinkAsync.fulfilled,
    effect: (action, { getState }) => {
      const state = getState();
      const payload = createEventPayload(state, {
        linkType: action.payload.platform,
      });
      captureAnalyticsEvent(PostHogEvents.SOCIAL_LINK_ADDED, payload);
    },
  });
}

// ============================================
// Utility Functions
// ============================================

/**
 * Extract eventId from event URL
 *
 * Event URLs typically have format: /events/{eventId} or similar
 * Returns undefined if unable to extract
 */
function extractEventIdFromUrl(eventUrl: string | undefined): string | undefined {
  if (!eventUrl) return undefined;

  try {
    // Parse URL and extract potential event ID from path
    const url = new URL(eventUrl, "https://placeholder.com");
    const pathParts = url.pathname.split("/").filter(Boolean);

    // Look for UUID pattern in path
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const eventId = pathParts.find((part) => uuidRegex.test(part));

    return eventId;
  } catch {
    return undefined;
  }
}

// ============================================
// Module Initialization
// ============================================

// Register all event listeners on module load
registerEventListeners();
